import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { relatedWorkProgressBar_versionProgress$key } from '@atlassian/jira-relay/src/__generated__/relatedWorkProgressBar_versionProgress.graphql';
import { DONE_COLOR, TODO_COLOR } from '../../common/constants.tsx';
import {
	Container,
	ProgressBarContainer,
	ProgressBarElementContainer,
	Bar,
} from '../../common/ui/styled.tsx';
import messages from './messages.tsx';

export type Props = {
	relatedWorkEdges: relatedWorkProgressBar_versionProgress$key;
};

export const RelatedWorkProgressBar = ({ relatedWorkEdges }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const edges = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment relatedWorkProgressBar_versionProgress on JiraVersionRelatedWorkV2Edge
			@relay(plural: true) {
				node @required(action: NONE) {
					... on JiraVersionRelatedWorkGenericLink {
						__typename
						url
					}
					... on JiraVersionRelatedWorkConfluenceReleaseNotes {
						__typename
						url
					}
					... on JiraVersionRelatedWorkNativeReleaseNotes {
						__typename
					}
				}
			}
		`,
		relatedWorkEdges,
	);

	const totalCount = edges.length;
	const numNonPlaceholders = totalCount
		? edges.filter((edge) => {
				switch (edge?.node.__typename) {
					case 'JiraVersionRelatedWorkNativeReleaseNotes':
						return true;
					case 'JiraVersionRelatedWorkGenericLink':
					case 'JiraVersionRelatedWorkConfluenceReleaseNotes':
						return edge.node.url !== null;
					default:
						return false;
				}
			}).length
		: 0;
	const numPlaceholders = totalCount - numNonPlaceholders;

	const fireAnalyticsEvent = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'relatedWorkProgressTooltip', {
			numOfItems: totalCount,
			numPlaceholders,
			numNonPlaceholders,
		});
	};

	if (totalCount === 0) {
		return (
			<Container>
				<ProgressBarContainer>
					<ProgressBarElementContainer
						totalCount={1}
						portionCount={1}
						isLast
						aria-label={formatMessage(messages.noRelatedWorkText)}
					>
						<Bar color={TODO_COLOR} isFirst isLast />
					</ProgressBarElementContainer>
				</ProgressBarContainer>
			</Container>
		);
	}

	return (
		<Container>
			<ProgressBarContainer>
				{numNonPlaceholders > 0 && (
					<ProgressBarElementContainer
						totalCount={totalCount}
						portionCount={numNonPlaceholders}
						aria-label={formatMessage(messages.nonPlaceholdersSegmentTooltipText, {
							numNonPlaceholders,
						})}
					>
						<Tooltip
							position="top"
							content={formatMessage(messages.nonPlaceholdersSegmentTooltipText, {
								numNonPlaceholders,
							})}
							onShow={(analyticsEvent: UIAnalyticsEvent) => fireAnalyticsEvent(analyticsEvent)}
						>
							<Bar color={DONE_COLOR} isFirst isLast={numPlaceholders === 0} />
						</Tooltip>
					</ProgressBarElementContainer>
				)}

				{numPlaceholders > 0 && (
					<ProgressBarElementContainer
						isLast
						totalCount={totalCount}
						portionCount={numPlaceholders}
						aria-label={formatMessage(messages.placeholdersSegmentTooltipText, {
							numPlaceholders,
						})}
					>
						<Tooltip
							position="top"
							content={formatMessage(messages.placeholdersSegmentTooltipText, {
								numPlaceholders,
							})}
							onShow={(analyticsEvent: UIAnalyticsEvent) => fireAnalyticsEvent(analyticsEvent)}
						>
							<Bar color={TODO_COLOR} isFirst={numNonPlaceholders === 0} isLast />
						</Tooltip>
					</ProgressBarElementContainer>
				)}
			</ProgressBarContainer>
		</Container>
	);
};
